import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, getToken, setToken } from '../services/api';
import { PLAN_GATING_ENABLED } from '../utils/plan';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function restoreSession() {
      const token = getToken();
      if (!token) {
        setIsReady(true);
        return;
      }
      try {
        const { user: restoredUser } = await api.get('/auth/me');
        if (!cancelled) setUser(restoredUser);
      } catch (error) {
        // Only discard the credential when the server actually rejected it. This used to
        // catch everything, so a backend restart or a dropped connection at load signed
        // people out and made them retype a password for a token that was still valid.
        // A network error means we don't know whether the session is good, and "don't
        // know" is not "expired".
        if (error.status === 401 || error.status === 403) setToken(null);
      } finally {
        if (!cancelled) setIsReady(true);
      }
    }
    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshAccounts = useCallback(async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'staff')) {
      setAccounts([]);
      return;
    }
    try {
      const { users } = await api.get('/users');
      setAccounts(users);
    } catch (error) {
      setAccounts([]);
    }
  }, [user]);

  useEffect(() => {
    refreshAccounts();
  }, [refreshAccounts]);

  const login = useCallback(async (email, password) => {
    try {
      const { token, user: loggedInUser } = await api.post('/auth/login', { email, password });
      setToken(token);
      setUser(loggedInUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const signup = useCallback(async (username, email, password) => {
    try {
      const { token, user: newUser } = await api.post('/auth/signup', { username, email, password });
      setToken(token);
      setUser(newUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const loginWithGoogleCredential = useCallback(async (credential) => {
    try {
      const { token, user: loggedInUser } = await api.post('/auth/google', { credential });
      setToken(token);
      setUser(loggedInUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  // Re-pulls the current user, e.g. after a subscription checkout changes
  // subscriptionPackage - keeps role/package-driven gating fresh without a re-login.
  const refreshMe = useCallback(async () => {
    try {
      const { user: refreshedUser } = await api.get('/auth/me');
      setUser(refreshedUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Checklist reminders are wall-clock times ("remind me at 20:00"), and the server
  // cannot know which clock that is - in production its own is UTC. So the browser
  // reports the zone it is in, once, and only when it differs from what is stored: the
  // person changes timezone far less often than they open the app. A failure here is
  // silent on purpose; the server falls back to its own zone, which is what it did
  // before this existed.
  useEffect(() => {
    if (!user) return;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timezone || timezone === user.timezone) return;
    api.patch('/auth/me', { timezone })
      .then(({ user: updatedUser }) => setUser(updatedUser))
      .catch(() => {});
  }, [user]);

  const isAuthenticated = !!user;
  const role = user?.role || null;
  // 'special' accounts get full Pro-equivalent access without ever subscribing -
  // every feature-gating check in the app should go through this, not a raw
  // subscriptionPackage comparison, so the two can't drift out of sync again.
  // Everything is free, so every account has full access - see utils/plan.js. The
  // role/package check is kept behind the flag so re-enabling gating restores the old
  // behaviour without having to reconstruct it.
  const isPro = !PLAN_GATING_ENABLED || role === 'special' || (user?.subscriptionPackage || 'free') !== 'free';

  const changeCredentials = useCallback(async (currentPassword, updates) => {
    try {
      const { user: updatedUser } = await api.patch('/auth/me', { currentPassword, ...updates });
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const deleteAccount = useCallback(async (password) => {
    try {
      await api.delete('/auth/me', { password });
      setToken(null);
      setUser(null);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const setAvatar = useCallback(async (avatarUrl) => {
    try {
      const { user: updatedUser } = await api.patch('/auth/me', { avatarUrl });
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const adminCreateAccount = useCallback(async ({ username, email, password, role: newRole, subscriptionPackage, subscriptionPurchasedAt }) => {
    try {
      await api.post('/users', { username, email, password, role: newRole, subscriptionPackage, subscriptionPurchasedAt });
      await refreshAccounts();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshAccounts]);

  const adminUpdateAccount = useCallback(async (id, updates) => {
    try {
      const { user: updatedUser } = await api.patch(`/users/${id}`, updates);
      await refreshAccounts();
      if (user && updatedUser.id === user.id) setUser(updatedUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshAccounts, user]);

  const adminDeleteAccount = useCallback(async (id) => {
    try {
      await api.delete(`/users/${id}`);
      await refreshAccounts();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshAccounts]);

  const value = useMemo(() => ({
    isAuthenticated,
    isReady,
    id: user?.id || null,
    username: user?.username || '',
    email: user?.email || '',
    avatarUrl: user?.avatarUrl || null,
    isGoogleAccount: !!user?.isGoogleAccount,
    role,
    subscriptionPackage: user?.subscriptionPackage || 'free',
    assistantPersonality: user?.assistantPersonality || 'wise',
    isPro,
    accounts,
    login,
    signup,
    loginWithGoogleCredential,
    logout,
    changeCredentials,
    deleteAccount,
    setAvatar,
    refreshMe,
    adminCreateAccount,
    adminUpdateAccount,
    adminDeleteAccount,
  }), [isAuthenticated, isReady, user, role, isPro, accounts, login, signup, loginWithGoogleCredential, logout, changeCredentials, deleteAccount, setAvatar, refreshMe, adminCreateAccount, adminUpdateAccount, adminDeleteAccount]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
